// Dependencies
import React, { useContext } from "react";

import { config, CSSTransition } from 'react-transition-group';
import { CSSTransitionProps } from "react-transition-group/CSSTransition";
import withMediaQuery from "../hooks/useMediaQuery";
import { usePopperTooltip } from 'react-popper-tooltip';

// Types
import { Types, Components } from '../types';

// Components
import { MinicartContext } from "../contexts/Minicart";
import Price from "./Price";
import { CurrentPrice, DisplayPrice, OriginalPrice } from "./Product/Price";
import styled, { css } from "styled-components";
import reactStringReplace from 'react-string-replace';
import _ from "lodash";
import { Helpers } from "../utils";
import { getBreakpoint } from "../utils/helpers";
import Icon from "./Icon";


const SidebarFooterActions = styled.div<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { actions: styles } } } }) => css`
	display: flex;
	flex-direction: column;

	${Helpers.clamp('padding', 1.35, 1.625, 5)}
	gap: 0.75em;
`)


const SidebarFooterSubtotalLabel = styled.div<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { subtotal: styles } } } }) => css`
	font-weight: 400;
	font-size: 1.25em;
	line-height: 1.15;
	color: ${styles.labelTextColor};
`)


const SidebarFooterSubtotal = styled.div<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { subtotal: styles } } } }) => css`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 2em 2em 1.25em;
	position:relative;            

	.wooptima-price {
		font-size: 1.625em;
		line-height: 1.15;
		color: ${styles.subtotalTextColor};
		font-weight:700;
		margin-left: 0.325em;
	}

	&::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 50%;
		transform: translateX(-50%);
		width: 3em;
		border-radius: 1em;
		height: 0.5em;
		background: #EEEEEE;
	}
	
`)


const SidebarFooterWrapper = styled.div<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: styles } } }) => css`
	width: 100%;
	&.enter {
			& {
				transition: all ${transition.duration} ${transition.timingFunction};
				transform: translateY(100%);
				position: absolute;
				bottom: 0;
			}
			&-active,
			&-done {
				transform: translateY(0);
			}
		}
		
		&.exit {
			& {
				transition: all ${transition.duration} ${transition.timingFunction};
				transform: translateY(0);
			}
			&-active,
			&-done {
				.wooptima-minicart-sidebar-footer__notices {
					z-index: -1;
				}
				position: absolute;
				bottom: 0;
				transform: translateY(100%);
			}
		}

		margin-top: auto;

		background-color: ${styles.background};
`)


export const ActionButton = styled.a<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { actions: { button: styles } } } } }) => css`
	all: unset;
	box-sizing: border-box;
	display: block;
	cursor: pointer;
	padding: ${styles.paddingBlock}em ${styles.paddingInline}em;
	text-align: center;

	background: ${styles.background};
	color: ${styles.textColor};
	border: ${styles.borderWidth}em solid ${styles.borderColor};
	border-radius: ${Helpers.getFormattedValue(styles.borderRadius)};
  
	font-weight: 400;
	${Helpers.clamp('font-size', 1.15, 1.25, 5)}
	transition: all ${transition.duration} ${transition.timingFunction};
	
	&:disabled {
	  opacity: .5; 
	  cursor: not-allowed;
	}
	
	&:hover {
		background-color: ${styles.hoverBackground ?? styles.background};
		border-color: ${styles.hoverBorderColor ?? styles.borderColor};
		color: ${styles.hoverTextColor ?? styles.textColor};
	}
`)

const CartButton = styled(ActionButton)<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { actions: { button: styles } } } } }) => css`
	padding: ${styles.cart.paddingBlock ?? styles.paddingBlock}em ${styles.cart.paddingInline ?? styles.paddingInline}em;
	background: ${styles.cart.background ?? styles.background};
	color: ${styles.cart.textColor ?? styles.textColor};
	border: ${styles.cart.borderWidth ?? styles.borderWidth}em solid ${styles.cart.borderColor ?? styles.borderColor};
	border-radius: ${Helpers.getFormattedValue(styles.cart.borderRadius ?? styles.borderRadius)};

	&:hover {
		${styles.cart.hoverBorderColor && `border-color: ${styles.cart.hoverBorderColor ?? styles.hoverBorderColor};`}
		${styles.cart.hoverTextColor && `color: ${styles.cart.hoverTextColor ?? styles.hoverTextColor};`}
		${styles.cart.hoverBackground && `background-color: ${styles.cart.hoverBackground ?? styles.hoverBackground};`}
	}
`)


const CheckoutButton = styled(ActionButton)<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { actions: { button: styles } } } } }) => css`
	padding: ${styles.checkout.paddingBlock ?? styles.paddingBlock}em ${styles.checkout.paddingInline ?? styles.paddingInline}em;
	background: ${styles.checkout.background ?? styles.background};
	color: ${styles.checkout.textColor ?? styles.textColor};
	border: ${styles.checkout.borderWidth ?? styles.borderWidth}em solid ${styles.checkout.borderColor ?? styles.borderColor};
	border-radius: ${Helpers.getFormattedValue(styles.checkout.borderRadius ?? styles.borderRadius)};

	&:hover {
		${styles.checkout.hoverBorderColor && `border-color: ${styles.checkout.hoverBorderColor ?? styles.hoverBorderColor};`}
		${styles.checkout.hoverTextColor && `color: ${styles.checkout.hoverTextColor ?? styles.hoverTextColor};`}
		${styles.checkout.hoverBackground && `background-color: ${styles.checkout.hoverBackground ?? styles.hoverBackground};`}
	}
`)


export const SidebarFooterNoticesWrapper = styled.div<any>(
	({ theme: { general: { transition, ...general }, components: { sidebarFooter: { notices: styles } } } }) => css`
	width: 100%;
	position: relative;
	height: max-content;
	&.enter {
			& {
				transition: all ${transition.duration} ${transition.timingFunction};
				opacity: 0;
				transform: translateY(100%);
			}
			&-active,
			&-done {
				opacity: 1;
				transform: translateY(0);
			}
		}
		
		&.exit {
			& {
				transition: all ${transition.duration} ${transition.timingFunction};
				opacity: 1;
				transform: translateY(100%);
			}
			&-active,
			&-done {
				opacity: 0;
				transform: translateY(0);
			}
		}
`)


const ShippingInfoNoticeWrapper = styled.div<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { notices: { shippingInfo: styles } } } } }) => css`
	background: #fff;
	border-top: 0.1em #eee solid;
	border-bottom: 0.1em #eee solid;
	padding: 0 1.15em;
	${Helpers.clamp('padding-top', 1.35, 1.625, 5)}
	${Helpers.clamp('padding-bottom', 1.35, 1.625, 5)}
	display: flex;
	align-items: center;
	gap: 0.75em;
	justify-content: space-between;
	${FreeShippingNoticeWrapper} {
		flex: 1 0 75%;
	}
	${ShippingInfoTooltipsWrapper} {
		flex: 1 0 25%;
	}
`)


const FreeShippingNoticeWrapper = styled.div<any>(({ state, theme: { general: { transition, ...general }, components: { sidebarFooter: { notices: { shippingInfo: styles } } } } }) => css`
	${FreeShippingProgressBar} {
		margin-top: 0.75em;
	}
`)


const FreeShippingText = styled.div<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { notices: { shippingInfo: styles } } } } }) => css`
	font-size: ${styles.freeShippingText.textSize}em;
	color: ${styles.freeShippingText.textColor};
	${CurrentPrice} {
		font-size: 1em;
	}
`)


const ShippingInfoTooltipsWrapper = styled.div<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { notices: { shippingInfo: styles } } } } }) => css`
	display: flex;
	align-items: center;
	gap: 0.5em;
	flex-wrap: wrap;
`)


const FreeShippingProgressBar = styled.div<any>(({ threshold, remaining, theme: { general: { transition, ...general }, components: { sidebarFooter: { notices: { shippingInfo: styles } } } } }) => css`
	position: relative;
	width: 100%;
	height: ${styles.progressBar.height}em;
	background: ${styles.progressBar.outerBackground};
	border-radius: ${Helpers.getFormattedValue(styles.progressBar.borderRadius)};

	&::before {
		content: '';
		position: absolute;
		top: ${(styles.progressBar.innerPadding)}em; // TODO: Assign min value
		left: ${(styles.progressBar.innerPadding)}em;
		background: ${styles.progressBar.innerBackground};
		border-radius: ${Helpers.getFormattedValue(styles.progressBar.borderRadius)};;
		width: calc( ${100 - Math.abs((remaining / threshold) * 100)}% - ${(styles.progressBar.innerPadding) * 2}em);
		height: calc(100% - ${(styles.progressBar.innerPadding) * 2}em); 
		transition: all ${transition.duration} ${transition.timingFunction};
	}
`)


export const FreeShippingNotice = ({ subtotal, config: { general, notices } }: any) => {
	const remainingAmount = React.useMemo(() => {
		const thresholdValue = notices.freeShipping.threshold - subtotal > 0 ? notices.freeShipping.threshold - subtotal : 0;
		return Helpers.getRoundedPrice(
			thresholdValue,
			Math.abs(general.roundingPrecision)
		);
	}, [subtotal, notices, general]);

	const noticeText = React.useMemo(() => {
		if (subtotal <= 0) {
			return reactStringReplace(
				notices.freeShipping.emptyText, '[threshold]',
				() => <DisplayPrice {...general} type="current">{remainingAmount.toFixed(Math.abs(general.decimals)).replace('.', ',')}</DisplayPrice>
			);
		}
		if (remainingAmount > 0) {
			return reactStringReplace(
				notices.freeShipping.remainingText, '[remaining]',
				() => <DisplayPrice {...general} type="current">{remainingAmount.toFixed(Math.abs(general.decimals)).replace('.', ',')}</DisplayPrice>
			);
		}
		return notices.freeShipping.fullfilledText;
	}, [remainingAmount, notices, general, subtotal]);

	return (
		<FreeShippingNoticeWrapper>
			<FreeShippingText>{noticeText}</FreeShippingText>
			{subtotal > 0 && <FreeShippingProgressBar {...{ threshold: notices.freeShipping.threshold, remaining: remainingAmount }} />}
		</FreeShippingNoticeWrapper>
	)

}


const ShippingInfoBoxWrapper = styled.div<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { notices: { shippingInfo: styles } } } } }) => css`
	display: flex;
	flex-direction: column;
	padding: 1em;
	
	&:not(:last-child) {
		border-bottom: 0.125em #eee solid;
	} 

	${ShippingInfoHeader} {
	   ${Helpers.clamp('margin-bottom', 0.325, 0.5, 3.25)};
	}

	p {
		color: ${styles.tooltipContent.descriptionColor};
		font-size: ${styles.textSize}
		margin: 0;
		line-height: 1.25;
	}
`)


const ShippingInfoHeader = styled.div<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { notices: { shippingInfo: styles } } } } }) => css`
	display: flex;
	align-items: center;
	gap: 1em;

	strong {
		${Helpers.clamp('font-size', 0.85, 1, 3.25)};
		color: ${styles.tooltipContent.headingColor};
	}
`)


const ShippingInfoPrices = styled.div<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { notices: { shippingInfo: styles } } } } }) => css`

	display: flex;
	align-items: center;

	${OriginalPrice},
	${CurrentPrice} {
		display: flex;
		align-items: center;
		border-radius: 0.25em;
		padding: 0.25em 0.5em;
		${Helpers.clamp('font-size', 0.625, 0.85, 3.25)};
	}

	${OriginalPrice} {
		color: ${styles.tooltipContent.originalPriceColor};
		background: ${styles.tooltipContent.originalPriceBackground};
		margin-right: 0.5em;
	}

	${CurrentPrice} {
		font-weight: 500;
		color: ${styles.tooltipContent.currentPriceColor};
		background: ${styles.tooltipContent.currentPriceBackground};
	}
`)


export const ShippingInfoBox = ({ title, description, prices, config }: any) => {
	const ShippingPrices = withMediaQuery((props: any) => (
		<ShippingInfoPrices {...props}>
			{_.isNumber(prices?.sale) && <DisplayPrice {...config.general} type="original">{(prices?.regular).toFixed(Math.abs(config.general.decimals))}</DisplayPrice>}
			<DisplayPrice {...config.general} type="current">{(prices?.sale ?? prices?.regular).toFixed(Math.abs(config.general.decimals)).replace('.', ',')}</DisplayPrice>
		</ShippingInfoPrices>
	))

	return (
		<ShippingInfoBoxWrapper>
			<ShippingInfoHeader>
				<strong>{title}</strong>
				{/** width >= 425px (NOTE: Sorry for this unDRY, bad day today...) */}
				<ShippingPrices minWidth={425} />
			</ShippingInfoHeader>
			<p>{description}</p>
				{/** width < 425px (NOTE: Sorry for this unDRY, bad day today...) */}
				<ShippingPrices maxWidth={425} style={{marginTop: '0.25em'}} />
		</ShippingInfoBoxWrapper>
	)
}


export const ShippingInfoNotice = ({ subtotal, config, translations }: any) => {
	return (
		<ShippingInfoNoticeWrapper>
			<FreeShippingNotice {...{ subtotal, config }} />
			{!_.isEmpty(config.notices.shippingInfo) &&
				<ShippingInfoTooltipsWrapper>
					{config.notices.shippingInfo.map(({ icon, content }: any, noticeIndex: number) => (
						<InfoTooltip key={`info_tooltip_${noticeIndex}`} {...{ icon }}>
							{content.map((infoBoxData: any, index: number) => <ShippingInfoBox key={`shipping_box_${noticeIndex}-${index}`} {...{ ...infoBoxData, config }} />)}
						</InfoTooltip>
					))}
				</ShippingInfoTooltipsWrapper>
			}
		</ShippingInfoNoticeWrapper>
	)
}


const InfoTooltipTriggerWrapper = styled.div<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { notices: { shippingInfo: styles } } } } }) => css`
	display: flex;
	align-items: center;
	justify-content: center;

	border-radius: ${Helpers.getFormattedValue(styles.tooltipTrigger.borderRadius)};
	background: ${styles.tooltipTrigger.background};
	color: ${styles.tooltipTrigger.iconColor};
	padding: ${styles.tooltipTrigger.size * 0.2}em;
	
	width: ${styles.tooltipTrigger.size}em;
	height: ${styles.tooltipTrigger.size}em;

	div, svg {
		width: ${styles.tooltipTrigger.size}em;
		height: ${styles.tooltipTrigger.size}em;
	}
`)


const InfoTooltipContainer = styled.div<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { notices: { shippingInfo: styles } } } } }) => css`
	z-index: 9999999;
	background: #fff;
	position: relative;
	border-radius: 0.5em;
	border: 0.125em #ddd solid;

	&[data-popper-placement=top] {
		${InfoTooltipArrow} {
			bottom: -1em;
		}
	}
	
	&[data-popper-placement=bottom] {
		${InfoTooltipArrow} {
			top: -1em;
		}
	}
`)


const InfoTooltipContentWrapper = styled.div<any>(({ theme: { general: { transition, ...general }, components: { shippingInfo: styles  } } }) => css`
	padding: 0.25em;
	font-size: 16px;
	position: relative;
	overflow: auto;

	&::-webkit-scrollbar {
		-webkit-appearance: none;
	}

	&::-webkit-scrollbar:vertical {
		width: 0.5em;
	}
	
	&::-webkit-scrollbar:horizontal {
		height: 0.5em;
	}

	&::-webkit-scrollbar-thumb {
		background-color: rgba(0, 0, 0, .25);
		border-radius: 1em;
		border: 0.125em solid #ffffff;
	}

	&::-webkit-scrollbar-track {
		border-radius: 1.5em;
		background-color: rgba(0, 0, 0, .05);
	}

	& > div {
		max-height: 32vh;
	}
	@media screen and (max-height: ${getBreakpoint('tablet', 'sm')}) {
		& > div {
			max-height: 25vh;
		}
	} 
`)


const InfoTooltipArrow = styled.span<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { notices: { shippingInfo: styles } } } } }) => css`
	position: absolute;
	background: transparent;
	left: 0;
	width: 10em;
	height: 1em;
`)


const InfoTooltipLabel = styled.span<any>(() => css`
	border-bottom: 0.1em currentColor solid;
`)


export const InfoTooltip = ({ label, icon, children }: any) => {
	const {
		getArrowProps,
		getTooltipProps,
		setTooltipRef,
		setTriggerRef,
		visible,
	} = usePopperTooltip({ trigger: 'hover', placement: 'top', interactive: true });

	return (<>
		<InfoTooltipTriggerWrapper ref={setTriggerRef}>
			{label && <InfoTooltipLabel>{label}</InfoTooltipLabel>}
			{icon && <Icon id={icon} />}
		</InfoTooltipTriggerWrapper>
		{visible && (<InfoTooltipContainer ref={setTooltipRef} {...getTooltipProps({ className: 'tooltip-container' })}>
			<InfoTooltipArrow {...getArrowProps()} />
			<InfoTooltipContentWrapper>
				<div>{children}</div>
			</InfoTooltipContentWrapper>
		 </InfoTooltipContainer>)
		}
	</>);

}


const AppliedDiscountCouponsList = styled.ul<any>(({ theme: { general: { transition, ...general }, components: { sidebarFooter: { notices: { shippingInfo: styles } } } } }) => css`
	background: #fff;
	border-bottom: 0.1em #eee solid;
	list-style: none;
	padding: 0 1.15em;
	${Helpers.clamp('padding-top', 1.15, 1.25, 5)}
	${Helpers.clamp('padding-bottom', 1.15, 1.25, 5)}
	display: flex !important;
	flex-direction: column;
	gap: 0.325em;
	margin: 0 !important;
`)


const AppliedDiscountCouponItem = styled.li<any>(() => css`
	display: flex !important;
	align-items: center;
	justify-content: space-between;
	${Helpers.clamp('font-size', 0.8, 0.9, 5)} !important;
`)


const SidebarFooter = ({ className }: any) => {
	const { loading, itemsSubtotal, itemsCount, config, translations, theme } = useContext(MinicartContext);

	const transitionProps: CSSTransitionProps = React.useMemo(() => ({
		in: itemsCount > 0,
		timeout: Number(theme.general.transition.duration.replace('ms', '')),
		unmountOnExit: true,
		mountOnEnter: true
	}), [theme.general.transition.duration, itemsCount])

	return (<>
		<CSSTransition {...transitionProps}>
			<SidebarFooterWrapper className={`${className}`}>

				<SidebarFooterNoticesWrapper className={`${className}__notices`}>
					<ShippingInfoNotice {...{ subtotal: itemsSubtotal, config, translations }} />
				</SidebarFooterNoticesWrapper>

				<SidebarFooterSubtotal className={`${className}__subtotal`}>
					<SidebarFooterSubtotalLabel className={`${className}__subtotal--label`}>{translations?.cartFooter.subtotal}</SidebarFooterSubtotalLabel>
					<Price
						{...{ loading }}
						{...{ config, translations, theme }}
						value={itemsSubtotal}
					/>
				</SidebarFooterSubtotal>

				<SidebarFooterActions className={`${className}__actions`}>
					<CartButton href={config?.links?.cart?.url}>{config?.links?.cart?.label}</CartButton>
					<CheckoutButton href={config?.links?.checkout?.url}>{config?.links?.checkout?.label}</CheckoutButton>
				</SidebarFooterActions>

			</SidebarFooterWrapper>
		</CSSTransition>
	</>)
}

export default SidebarFooter;